import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import styles from "./FormationDetail.module.sass";
import { useLocation, useParams, Link } from "react-router-dom";

// Screens
import NameAndDescription from "./NameAndDescription";
import Promotion from "./Promotion";

// Components
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import Icon from "../../components/Icon";
import Actions from "../../components/Actions";

// Services
import { getFormationById, updateFormation } from "../../services/formation.service";
import { showSuccess, showError } from "../../services/toast.service";


// data
const navigation = ["Formation", "Promotions"];

const FormationDetail = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [formation, setFormation] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const location = useLocation();
  const { id } = useParams();


  // Fonction pour rafraîchir les données de la formation
  const refreshFormation = useCallback(async () => {
    try {
      if (id) {
        const formationData = await getFormationById(id);
        setFormation(formationData);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de la formation:', error);
    }
  }, [id]);

  // Fonction pour changer le statut de la formation
  const handleStatusChange = async () => {
    if (!formation) return;
    
    try {
      setUpdatingStatus(true);
      const newStatus = formation.status === 'Active' ? 'Inactive' : 'Active';
      const actionText = newStatus === 'Active' ? 'activée' : 'désactivée';
      
      await updateFormation(formation.id, { status: newStatus });
      await refreshFormation();
      
      showSuccess(`Formation ${actionText} avec succès!`);
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      showError(error.message || 'Erreur lors du changement de statut');
    } finally {
      setUpdatingStatus(false);
    }
  };

  useEffect(() => {
    if (location.state?.formation) {
      setFormation(location.state.formation);
      return;
    }
    const loadFormation = async () => {
      try {
        const data = await getFormationById(id);
        setFormation(data);
      } catch (err) {
        console.error('Erreur:', err);
      }
    };
    loadFormation();
  }, [id, location.state]);

  return (
    <>
      <div className={styles.shop}>
        <Link className={cn("button-white", styles.button)} to="/admin/formations">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <Card className={styles.card}>
          {formation && (
            <div className={styles.profile}>
              <div className={styles.details}>
                <div className={styles.avatar}>
                  <img src={"/images/content/formation_logo.png"} alt={formation.name} />
                </div>
                <div className={styles.wrap}>
                  <div className={cn("h4", styles.man)}>
                    {formation.exact_title_of_qualification}
                  </div>
                  <div className={styles.login}>
                    Code RNCP: {formation.rncp_code}
                  </div>
                </div>
              </div>

              <div className={styles.statusContainer}>
                <div className={styles.statusGroup}>
                  <div className={styles.login}>Code RNCP</div>
                  <div className={cn(
                    styles.statusBadge,
                    'status-type',
                    'status-blue-dark'
                  )}>
                    {formation.rncp_code}
                  </div>
                </div>

                <div className={styles.statusGroup}>
                  <div className={styles.login}>Code diplôme</div>
                  <div className={cn(
                    styles.statusBadge,
                    'status-type',
                    'status-blue-dark'
                  )}>
                    {formation.diploma_code}
                  </div>
                </div>

                <div className={styles.statusGroup}>
                  <div className={styles.login}>Statut</div>
                  <div className={cn(
                    styles.statusBadge,
                    'status-type',
                    (() => {
                      const statusLower = formation.status?.toLowerCase();
                      switch (statusLower) {
                        case 'active':
                          return 'status-green-dark';
                        case 'inactive':
                          return 'status-red-dark';
                        case 'brouillon':
                          return 'status-red-dark';
                        default:
                          return 'status-grey';
                      }
                    })()
                  )}>
                    {formation.status === 'Active' ? 'Actif' : formation.status === 'Inactive' ? 'Inactif' : formation.status}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={styles.control}>
            <div className={cn(styles.nav, styles.desktopNav)}>
              {navigation.map((x, index) => (
                <button
                  className={cn(styles.link, {
                    [styles.active]: index === activeIndex,
                  })}
                  onClick={() => setActiveIndex(index)}
                  key={index}
                >
                  {x}
                </button>
              ))}
            </div>
            
            <div className={styles.actionContainer}>
              {updatingStatus ? (
                <div className={styles.loading}>Mise à jour en cours...</div>
              ) : (
                <Actions
                  className={styles.actions}
                  classActionsHead={styles.actionsHead}
                  items={[
                    {
                      title: formation?.status === 'Active' ? 'Désactiver la formation' : 'Activer la formation',
                      action: handleStatusChange
                    }
                  ]}
                  small
                />
              )}
            </div>

            <div className={styles.mobileNav}>
              <Dropdown
                className={styles.dropdown}
                value={navigation[activeIndex]}
                setValue={(value) => {
                  const index = navigation.findIndex(x => x === value);
                  if (index !== -1) setActiveIndex(index);
                }}
                options={navigation}
              />
            </div>
          </div>
        </Card>

        <div className={styles.wrap}>
          {activeIndex === 0 && formation && <NameAndDescription formation={formation} />}
          {activeIndex === 1 && formation && <Promotion formation={formation} />}
        </div>
      </div>
    </>
  );
};

export default FormationDetail;